/**
 * Central MU API hooks
 *
 * TanStack Query hooks for the central-MU endpoints added in PRs 3.1–3.4:
 *   - /api/instances/{serviceId}/*      (replaces /api/target/*)
 *   - /api/ontology/migrate             (new free-form ontology flow)
 *   - /api/admin/users/lookup           (pre-flight match across instances)
 *   - /api/admin/users/{id}/sync        (force-resync one user's access)
 *   - /api/admin/users/sync-all         (force-resync everyone)
 *
 * Lives alongside lib/api.ts as an additive file so the existing API surface keeps working
 * unchanged during the transition.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { axiosClient } from "./axiosClient";
import { handleApiError } from "./api";

const ADMIN_USERS_KEY = ["admin", "users"] as const;

// ---------- Shared types ----------

export type Instance = {
  id: number;
  name: string;
  baseUrl: string;
  active: boolean;
  /** Login username (per-row override or the server-wide system user); the password is never returned to the browser. */
  username?: string | null;
  /** True when credentials are available — per-row auth_config or the server-configured system defaults. */
  hasCredentials?: boolean;
  serviceType?: string | null; // legacy SOURCE/TARGET enum — ignore in UI
};

export type Facility = {
  id: string;
  name: string;
  [k: string]: unknown;
};

export type UseCase = {
  id: string;
  name: string;
  [k: string]: unknown;
};

export type PublishedChecklist = {
  id: string;
  name: string;
  code?: string;
  [k: string]: unknown;
};

export type TargetUser = {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  employeeId?: string;
  [k: string]: unknown;
};

// ---------- /api/connected-services (already exists) ----------

/**
 * All registered instances, regardless of legacy service_type.
 *
 * Reuses the same query key as the legacy {@code useConnectedServices} hook in lib/api.ts so
 * cache is shared — when the existing Workflow page has already loaded instances, this hook
 * sees them too instead of double-fetching.
 */
export const useInstances = () =>
  useQuery<Instance[]>({
    queryKey: ["connectedServices"],
    queryFn: async () => {
      const response = await axiosClient.get("/api/connected-services");
      const data = response.data;
      if (Array.isArray(data)) return data;
      // Some response shapes wrap arrays under `.data` — be defensive.
      if (data && Array.isArray((data as { data?: unknown }).data)) {
        return (data as { data: Instance[] }).data;
      }
      return [];
    },
    staleTime: 60_000,
  });

// ---------- /api/connected-services CRUD ----------

export type ConnectedServiceRequest = {
  name: string;
  /** Normalized server-side (https:// + api. host handling) and verified with a real login before saving. */
  baseUrl: string;
  /** Optional per-instance override; omit to use the server-configured system-user account. */
  username?: string;
  /** base64-encoded (btoa). Optional override; on update, leave blank to keep the stored password. */
  password?: string;
  /** Optional per-instance login path override. */
  loginPath?: string;
};

export type TestConnectionRequest = {
  /** When editing without re-entering secrets, pass the instance id to fall back to stored creds. */
  id?: number;
  name?: string;
  baseUrl?: string;
  username?: string;
  password?: string;
  loginPath?: string;
};

export type TestConnectionResult = {
  success: boolean;
  message: string;
};

export const useCreateInstance = () => {
  const queryClient = useQueryClient();
  return useMutation<Instance, unknown, ConnectedServiceRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post("/api/connected-services", request);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connectedServices"] }),
    onError: (error) => handleApiError(error, "Failed to create instance"),
  });
};

export const useUpdateInstance = () => {
  const queryClient = useQueryClient();
  return useMutation<Instance, unknown, { id: number; body: ConnectedServiceRequest }>({
    mutationFn: async ({ id, body }) => {
      const response = await axiosClient.put(`/api/connected-services/${id}`, body);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connectedServices"] }),
    onError: (error) => handleApiError(error, "Failed to update instance"),
  });
};

export const useDeleteInstance = () => {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, number>({
    mutationFn: async (id) => {
      await axiosClient.delete(`/api/connected-services/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connectedServices"] }),
    onError: (error) => handleApiError(error, "Failed to remove instance"),
  });
};

/**
 * Verify credentials against streem-backend. Does not invalidate any cache — it persists nothing.
 * The mutation itself never rejects on a failed connection; inspect {@code data.success}.
 */
export const useTestConnection = () =>
  useMutation<TestConnectionResult, unknown, TestConnectionRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post(
        "/api/connected-services/test-connection",
        request
      );
      return response.data;
    },
    onError: (error) => handleApiError(error, "Connection test failed"),
  });

// ---------- /api/instances/{serviceId}/* ----------

export const useInstanceFacilities = (serviceId: number | null | undefined) =>
  useQuery<Facility[]>({
    queryKey: ["instance", serviceId, "facilities"],
    queryFn: async () => {
      const response = await axiosClient.get(
        `/api/instances/${serviceId}/facilities`
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!serviceId,
    staleTime: 30_000,
  });

export const useInstanceUseCases = (
  serviceId: number | null | undefined,
  facilityId: string | null | undefined,
  enabled: boolean = true
) =>
  useQuery<UseCase[]>({
    queryKey: ["instance", serviceId, "use-cases", facilityId],
    queryFn: async () => {
      const response = await axiosClient.get(
        `/api/instances/${serviceId}/use-cases`,
        { params: { facilityId } }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: enabled && !!serviceId && !!facilityId,
  });

export type ChecklistSearchField = "name" | "code";

export const useInstancePublishedChecklists = (
  serviceId: number | null | undefined,
  facilityId: string | null | undefined,
  useCaseId: string | null | undefined,
  // Free-text search applied server-side; searchBy narrows it to checklist name or code
  // (the human-facing process ID). Omitted, both fields are matched.
  query?: string,
  searchBy?: ChecklistSearchField
) =>
  useQuery<PublishedChecklist[]>({
    queryKey: ["instance", serviceId, "checklists", facilityId, useCaseId, query || "", searchBy || ""],
    queryFn: async () => {
      const response = await axiosClient.get(
        `/api/instances/${serviceId}/published-checklists`,
        {
          params: {
            facilityId,
            useCaseId,
            query: query || undefined,
            searchBy: query ? searchBy : undefined,
          },
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!serviceId && !!facilityId && !!useCaseId,
  });

export const useInstanceUsers = (
  serviceId: number | null | undefined,
  facilityId: string | null | undefined,
  filters?: string
) =>
  useQuery<TargetUser[]>({
    queryKey: ["instance", serviceId, "users", facilityId, filters],
    queryFn: async () => {
      const response = await axiosClient.get(
        `/api/instances/${serviceId}/users`,
        { params: { facilityId, filters } }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!serviceId && !!facilityId,
  });

// ---------- /api/ontology/migrate (free-form, all-in-one) ----------

export type OntologyMigrationRequest = {
  sourceServiceId: number;
  targetServiceId: number;
  sourceFacilityId: string;
  targetFacilityId: string;
  objectTypeIds: string[];
};

export type OntologyMigrationResult = {
  workflowId: number | null;
  objectTypesRequested: number;
  objectTypesCreatedOnTarget: number;
  objectTypesAlreadyOnTarget: number;
  propertiesAdded: number;
  relationsAdded: number;
  entityObjectsCreatedOnTarget: number;
  entityObjectsAlreadyOnTarget: number;
  errors: string[];
};

export const useOntologyMigrate = () =>
  useMutation<OntologyMigrationResult, unknown, OntologyMigrationRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post("/api/ontology/migrate", request);
      return response.data;
    },
    onError: (error) => handleApiError(error, "Ontology migration failed"),
  });

// ---------- /api/action-effect/* (Action Effect Migration) ----------

export type ActionEffectObjectType = {
  id: string;
  externalId: string;
  displayName: string;
};

/**
 * A trigger document as returned by streem (ObjectIds already stringified). We type the fields the
 * UI reads; the rest of the raw document rides along untouched for migration.
 */
export type ActionEffectTrigger = {
  _id: string;
  triggerName?: string;
  description?: string;
  triggerType?: string;
  resourceConfig?: {
    eventType?: string;
    updateCondition?: {
      targetType?: string;
      targetExternalId?: string;
      targetInputType?: string;
      changeMode?: string;
    };
  };
  effects?: Array<Record<string, unknown>>;
  [k: string]: unknown;
};

export const useActionEffectObjectTypes = (serviceId: number | null | undefined) =>
  useQuery<ActionEffectObjectType[]>({
    queryKey: ["action-effect", serviceId, "object-types"],
    queryFn: async () => {
      const response = await axiosClient.get("/api/action-effect/object-types", {
        params: { serviceId },
      });
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!serviceId,
    staleTime: 30_000,
  });

export const useActionEffectTriggers = (
  serviceId: number | null | undefined,
  objectTypeId: string | null | undefined
) =>
  useQuery<ActionEffectTrigger[]>({
    queryKey: ["action-effect", serviceId, "triggers", objectTypeId],
    queryFn: async () => {
      const response = await axiosClient.get("/api/action-effect/triggers", {
        params: { serviceId, objectTypeId },
      });
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!serviceId && !!objectTypeId,
    staleTime: 0,
  });

export type ActionEffectMigrationRequest = {
  sourceServiceId: number;
  targetServiceId: number;
  sourceObjectTypeId: string;
  triggerIds: string[];
  /** Source/target facility — recorded on the audit trail; default to global "-1" when omitted. */
  sourceFacilityId?: string;
  sourceFacilityName?: string;
  targetFacilityId?: string;
  targetFacilityName?: string;
  /** When true, migrate the ObjectType's ontology to the target first if a dependency is missing. */
  autoMigrateObjectType?: boolean;
};

export type ActionEffectMigrationResult = {
  objectTypeExternalId: string;
  targetObjectTypeId: string | null;
  triggersRequested: number;
  triggersMigrated: number;
  triggersSkipped: number;
  objectTypeMigrated: boolean;
  errors: string[];
};

export const useActionEffectMigrate = () =>
  useMutation<ActionEffectMigrationResult, unknown, ActionEffectMigrationRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post("/api/action-effect/migrate", request);
      return response.data;
    },
    onError: (error) => handleApiError(error, "Action effect migration failed"),
  });

// ---------- /api/admin/users ----------

export type LookupField = "email" | "username" | "employeeId";

export type UserLookupRequest = {
  query: string;
  /** Which field to match on. */
  field: LookupField;
};

export type UserLookupMatch = {
  instanceId: number;
  instanceName: string;
  remoteUserId: string | null;
  remoteEmail: string | null;
  remoteEmployeeId: string | null;
  remoteUsername: string | null;
  firstName: string | null;
  lastName: string | null;
  matchedBy: string;
};

export type UserLookupResult = {
  query: string | null;
  matchedInstances: UserLookupMatch[];
};

export const useUserLookup = () =>
  useMutation<UserLookupResult, unknown, UserLookupRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post("/api/admin/users/lookup", request);
      return response.data;
    },
    onError: (error) => handleApiError(error, "User lookup failed"),
  });

// ---------- User records (list + CRUD) ----------

export type UserType = "DEFAULT" | "LOCAL" | "AZURE";

export type UserState = "INVITED" | "INVITE_EXPIRED" | "ACTIVE" | "ARCHIVED";

export type AdminUserInstance = {
  connectedServiceId: number;
  serviceName: string | null;
  remoteUserId: string | null;
  remoteEmail: string | null;
  remoteEmployeeId: string | null;
  matchedBy: string | null;
};

export type AdminUser = {
  id: number;
  email: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  employeeId: string | null;
  userType: UserType;
  /** Lifecycle state; replaces the old {@code enabled} flag. */
  state: UserState;
  /** Transient lockout after repeated failed logins; clears on its own. */
  locked: boolean;
  failedLoginAttempts?: number | null;
  lockedAt?: string | null;
  lastLoginAt: string | null;
  instanceIds: number[];
  /** Only present on the single-user (GET /{id}) response. */
  instances?: AdminUserInstance[];
};

/**
 * One-time link fields piggybacked on user responses whenever the backend issued a fresh
 * invite/reset link (LOCAL create, resend invite, unarchive of a never-activated user,
 * AZURE→LOCAL switch). The link is shown exactly once — it cannot be fetched again.
 */
export type IssuedLinkFields = {
  inviteLink?: string;
  emailSent?: boolean;
  expiresInMinutes?: number;
};

export type AdminUserWithLink = AdminUser & IssuedLinkFields;

export type CreateUserRequest = {
  email: string;
  username?: string;
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  userType: "LOCAL" | "AZURE";
  /** The field the admin searched by — access is seeded by matching on this field. */
  searchField?: LookupField;
};

export type UpdateUserRequest = {
  firstName?: string;
  lastName?: string;
  employeeId?: string;
  username?: string;
};

export const useUsers = () =>
  useQuery<AdminUser[]>({
    queryKey: ADMIN_USERS_KEY,
    queryFn: async () => {
      const response = await axiosClient.get("/api/admin/users");
      return Array.isArray(response.data) ? response.data : [];
    },
    staleTime: 30_000,
  });

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation<AdminUserWithLink, unknown, CreateUserRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post("/api/admin/users", request);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onError: (error) => handleApiError(error, "Failed to create user"),
  });
};

/** Resend (or revive) an invite; the response carries a fresh one-time inviteLink. */
export const useResendInvite = () => {
  const queryClient = useQueryClient();
  return useMutation<AdminUserWithLink, unknown, number>({
    mutationFn: async (id) => {
      const response = await axiosClient.post(`/api/admin/users/${id}/invite`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onError: (error) => handleApiError(error, "Failed to resend the invite"),
  });
};

/** Revoke a pending invite: the link stops working; resend can revive it later. */
export const useRevokeInvite = () => {
  const queryClient = useQueryClient();
  return useMutation<AdminUser, unknown, number>({
    mutationFn: async (id) => {
      const response = await axiosClient.delete(`/api/admin/users/${id}/invite`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onError: (error) => handleApiError(error, "Failed to revoke the invite"),
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation<AdminUser, unknown, { id: number; body: UpdateUserRequest }>({
    mutationFn: async ({ id, body }) => {
      const response = await axiosClient.put(`/api/admin/users/${id}`, body);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onError: (error) => handleApiError(error, "Failed to update user"),
  });
};

/** Archive (soft-delete) a user. Pending invite/reset links are invalidated. */
export const useArchiveUser = () => {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, number>({
    mutationFn: async (id) => {
      await axiosClient.delete(`/api/admin/users/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onError: (error) => handleApiError(error, "Failed to archive user"),
  });
};

/**
 * Unarchive a user. If they never set a password they are re-invited and the response carries a
 * fresh inviteLink.
 */
export const useUnarchiveUser = () => {
  const queryClient = useQueryClient();
  return useMutation<AdminUserWithLink, unknown, number>({
    mutationFn: async (id) => {
      const response = await axiosClient.post(`/api/admin/users/${id}/unarchive`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onError: (error) => handleApiError(error, "Failed to unarchive user"),
  });
};

// ---------- Instance-access sync ----------

export type InstanceSyncEntry = {
  instanceId: number;
  instanceName: string | null;
};

/**
 * Result of reconciling one user's access against the live instances. `errors` lists instances
 * that couldn't be reached — their access rows are left untouched, never revoked.
 */
export type InstanceSyncSummary = {
  added: InstanceSyncEntry[];
  removed: InstanceSyncEntry[];
  unchanged: InstanceSyncEntry[];
  errors: InstanceSyncEntry[];
};

export type AdminUserWithSync = AdminUser & { sync: InstanceSyncSummary };

/** Per-user entry of the sync-all response; `syncFailed` replaces `sync` when that user errored. */
export type SyncAllEntry = Partial<AdminUserWithSync> & {
  id: number;
  email?: string;
  syncFailed?: string;
};

/** Re-sync one user's instance access (grants new matches, revokes definitive no-matches). */
export const useSyncUserInstances = () => {
  const queryClient = useQueryClient();
  return useMutation<AdminUserWithSync, unknown, number>({
    mutationFn: async (id) => {
      const response = await axiosClient.post(`/api/admin/users/${id}/sync`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onError: (error) => handleApiError(error, "Failed to sync instance access"),
  });
};

/** Re-sync instance access for every syncable user (skips DEFAULT and ARCHIVED). */
export const useSyncAllUsers = () => {
  const queryClient = useQueryClient();
  return useMutation<SyncAllEntry[], unknown, void>({
    mutationFn: async () => {
      const response = await axiosClient.post("/api/admin/users/sync-all");
      return Array.isArray(response.data) ? response.data : [];
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onError: (error) => handleApiError(error, "Failed to sync instance access"),
  });
};

export type ResetPasswordResult = {
  resetLink: string;
  emailSent: boolean;
  expiresInMinutes: number;
};

/**
 * Issue a one-time password-reset link. The admin never chooses the password — the user sets
 * their own via the link.
 */
export const useResetPassword = () =>
  useMutation<ResetPasswordResult, unknown, number>({
    mutationFn: async (id) => {
      const response = await axiosClient.post(`/api/admin/users/${id}/reset-password`);
      return response.data;
    },
    onError: (error) => handleApiError(error, "Failed to issue a reset link"),
  });

export const useChangeAuthType = () => {
  const queryClient = useQueryClient();
  return useMutation<AdminUserWithLink, unknown, { id: number; newType: "LOCAL" | "AZURE" }>({
    mutationFn: async ({ id, newType }) => {
      const response = await axiosClient.post(
        `/api/admin/users/${id}/change-auth-type`,
        { newType }
      );
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onError: (error) => handleApiError(error, "Failed to change auth type"),
  });
};

export const useChangeOwnPassword = () =>
  useMutation<void, unknown, { currentPassword: string; newPassword: string }>({
    mutationFn: async (body) => {
      await axiosClient.post("/api/users/me/change-password", body);
    },
    onError: (error) => handleApiError(error, "Failed to change password"),
  });

// ---------- Password policy ----------

export type PasswordPolicy = {
  minimumPasswordLength: number;
  minimumUppercaseCharacters: number;
  minimumLowercaseCharacters: number;
  minimumNumericCharacters: number;
  minimumSpecialCharacters: number;
};

/** DB-configured password policy, for inline hints on the change-password form. */
export const usePasswordPolicy = (enabled: boolean = true) =>
  useQuery<PasswordPolicy>({
    queryKey: ["password-policy"],
    queryFn: async () => {
      const response = await axiosClient.get("/api/password-policy");
      return response.data;
    },
    enabled,
    staleTime: 5 * 60_000,
  });

export type MigrationSelectionConfig = {
  multiChecklistSelectionEnabled: boolean;
};

/**
 * DB-configured migration selection settings (utility_settings row, edited directly in the DB).
 * multiChecklistSelectionEnabled=false switches the checklist selector to single-select.
 */
export const useMigrationSelectionConfig = () =>
  useQuery<MigrationSelectionConfig>({
    queryKey: ["migration-selection-config"],
    queryFn: async () => {
      const response = await axiosClient.get("/api/settings/migration-selection");
      return response.data;
    },
    staleTime: 5 * 60_000,
  });

// ---------- Public invite / set-password (no auth) ----------

export type InviteDetails =
  | {
      valid: true;
      purpose: "INVITE" | "PASSWORD_RESET";
      email: string | null;
      username: string | null;
      firstName: string | null;
      lastName: string | null;
      passwordPolicy: PasswordPolicy;
    }
  | {
      valid: false;
      reason: "INVALID" | "EXPIRED";
    };

/**
 * Peek at an invite/reset token for the set-password page. Read-only — never consumes the token.
 * Invalid tokens still come back 200 with {@code valid:false}, so errors here are network-level.
 */
export const useInviteDetails = (token: string | null | undefined) =>
  useQuery<InviteDetails>({
    queryKey: ["invite-details", token],
    queryFn: async () => {
      const response = await axiosClient.get(`/v1/auth/invites/${token}`);
      return response.data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

/**
 * Complete an invite or password reset. Errors are surfaced inline on the set-password page, so
 * no toast here — the caller reads {@code error.response.data.message}.
 */
export const useCompleteInvite = () =>
  useMutation<{ message: string }, unknown, { token: string; password: string }>({
    mutationFn: async (body) => {
      const response = await axiosClient.post("/v1/auth/invites/complete", body);
      return response.data;
    },
  });

// ---------- Public password recovery (no auth) ----------

/** Email-link recovery. Always resolves with the same generic message (no user enumeration). */
export const usePasswordResetRequest = () =>
  useMutation<{ message: string }, unknown, { identifier: string }>({
    mutationFn: async (body) => {
      const response = await axiosClient.post("/v1/auth/password-reset/request", body);
      return response.data;
    },
  });

// ---------- Linked-checklist migration ----------
// Backs the "Linked Process Migration" workflow. Mirrors the backend records in
// com.leucine.onboarding.server.dto (LinkedChecklist*). IDs are strings at the API boundary;
// resolve/migrate take numeric checklist ids (the source ancestorId).

export type LinkedChecklistRef = { id: string; name: string; code: string; useCaseId: string };
export type LinkedChecklistStatus = "PRESENT" | "PROPOSED_MATCH" | "MISSING";

export type LinkedChecklistNode = {
  source: LinkedChecklistRef;
  /** null for a root (the parent process being migrated). */
  status: LinkedChecklistStatus | null;
  /** set for PRESENT (exact) / PROPOSED_MATCH (suggested); null for MISSING and roots. */
  proposedTarget: LinkedChecklistRef | null;
  depth: number;
  parentSourceId: string | null;
  /** true when the target already has this checklist (same id/ancestorId) — migrated in a prior run. */
  alreadyMigrated?: boolean;
  /**
   * true on a child that itself links other checklists. Only one parent→child level is supported:
   * its own children are not expanded and it cannot take a MIGRATE decision — migrate it
   * individually first, then link it.
   */
  hasNestedLinks?: boolean;
  children: LinkedChecklistNode[];
};

export type LinkedChecklistResolveRequest = {
  sourceServiceId: number;
  sourceFacilityId: string;
  checklistIds: string[];
  targetServiceId: number;
  targetFacilityId: string;
};
export type LinkedChecklistResolutionResult = { tree: LinkedChecklistNode[] };

export const useLinkedChecklistResolve = () =>
  useMutation<LinkedChecklistResolutionResult, unknown, LinkedChecklistResolveRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post("/api/linked-checklist/resolve", request);
      return response.data;
    },
    onError: (error) => handleApiError(error, "Failed to resolve linked checklists"),
  });

/* ---------- linked-process scan (selection step) ---------- */

/** One selected checklist's linked-process flag from `POST /api/linked-checklist/scan`. */
export type LinkedChecklistScanEntry = {
  checklistId: string;
  checklistName: string | null;
  /** false when streem's export omitted the checklist (only PUBLISHED checklists export). */
  foundInExport: boolean;
  hasLinkedProcess: boolean;
  /** Direct linked refs only — the full tree is discovered later by the resolve step. */
  children: LinkedChecklistRef[];
};
export type LinkedChecklistScanResult = { entries: LinkedChecklistScanEntry[] };
export type LinkedChecklistScanRequest = {
  sourceServiceId: number;
  sourceFacilityId: string;
  checklistIds: string[];
};

/**
 * Batch linked-process scan when leaving the selection step: flags which selected checklists
 * configure a linked process (one export call server-side, no recursion). No error toast here —
 * the caller decides between degrading to ungrouped routing (infra failure) and failing closed (403).
 */
export const useLinkedChecklistScan = () =>
  useMutation<LinkedChecklistScanResult, unknown, LinkedChecklistScanRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post("/api/linked-checklist/scan", request);
      return response.data;
    },
  });

/* ---------- linked-checklist ontology dependency preview ---------- */

export type OntologyType = "OBJECT_TYPE" | "OBJECT" | "RELATION" | "PROPERTY" | "PROPERTY_OPTIONS";
export type OntologySyncStatus = "MATCH" | "MISSING" | "CONFLICT" | "SYNC_FAILED";

export type OntologyRecord = {
  id: string | null;
  externalId: string | null;
  displayName: string | null;
  type: OntologyType;
  status: OntologySyncStatus | null;
  objectTypeName: string | null;
  referenceObjectTypeId: string | null;
  referencePropertyId: string | null;
};

export type LinkedChecklistOntologyGroup = {
  checklistId: string;
  checklistName: string | null;
  ontologyRecords: OntologyRecord[];
};

export type LinkedChecklistOntologyPreview = {
  perChecklist: LinkedChecklistOntologyGroup[];
  aggregated: OntologyRecord[];
};

export type LinkedChecklistOntologyPreviewRequest = {
  sourceServiceId: number;
  sourceFacilityId: string;
  /** All checklists in the tree (parent + children, including already-present) so each one's
   *  referenced object types/objects are shown with MATCH/MISSING/CONFLICT against the target. */
  checklistIds: string[];
  targetServiceId: number;
  targetFacilityId: string;
  includeObjects?: boolean;
};

/** POST /api/linked-checklist/ontology-preview — dry-run; imports nothing. */
export const useLinkedChecklistOntologyPreview = () =>
  useMutation<LinkedChecklistOntologyPreview, unknown, LinkedChecklistOntologyPreviewRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post("/api/linked-checklist/ontology-preview", request);
      return response.data;
    },
    onError: (error) => handleApiError(error, "Failed to preview ontology dependencies"),
  });

export type ChecklistSearchItem = {
  id: string;
  code?: string;
  name: string;
  useCaseId?: string;
  useCaseName?: string;
  versionId?: string;
  ancestorId?: string;
  versionNumber?: number;
  [k: string]: unknown;
};

/**
 * Free-text search of published checklists on an instance (facility-scoped). Optionally filtered by
 * {@code useCaseId} — the backend pages through and keeps only checklists in that use case.
 */
export const useChecklistSearch = (
  serviceId: number | null | undefined,
  facilityId: string | null | undefined,
  query: string,
  enabled = true,
  useCaseId?: string | null
) =>
  useQuery<ChecklistSearchItem[]>({
    queryKey: ["instance", serviceId, "checklist-search", facilityId, query, useCaseId ?? null],
    queryFn: async () => {
      const response = await axiosClient.get(`/api/instances/${serviceId}/checklists/search`, {
        params: { facilityId, query: query || undefined, useCaseId: useCaseId || undefined, page: 0, size: 50 },
      });
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: enabled && !!serviceId && !!facilityId,
  });

export type LinkedChecklistAction = "LINK_EXISTING" | "MIGRATE";
export type LinkedChecklistDecision = {
  /** source checklist id of the linked child (its ancestorId). String to preserve precision. */
  sourceChecklistId: string;
  action: LinkedChecklistAction;
  /** required for LINK_EXISTING (target checklist ancestorId); ignored for MIGRATE. */
  targetChecklistId: string | null;
  /** required for MIGRATE: target use case (within the parent's target facility — children never
   *  cross facilities) the child imports into; ignored for LINK_EXISTING. */
  targetUseCaseId?: string | null;
};
export type LinkedChecklistMigrateRequest = {
  sourceServiceId: number;
  sourceFacilityId: string;
  checklistIds: string[];
  targetServiceId: number;
  targetFacilityId: string;
  targetAuthorIds?: string;
  targetUseCaseId?: string;
  decisions: LinkedChecklistDecision[];
  /** When set, the (single) selected checklist imports as a REVISION of this target checklist. */
  targetChecklistId?: string;
  /** SOURCE use case of the selected parent(s) — audits render its name (children use their resolve-tree ref). */
  sourceUseCaseId?: string;
  /** User-entered workflow description — shown on the migration-logs header of each tracking workflow. */
  description?: string;
};
export type LinkedChecklistMigrationOutcome = {
  sourceId: string;
  action: LinkedChecklistAction;
  targetChecklistId: string | null;
  status: "SUCCESS" | "LINKED" | "SKIPPED" | "FAILED" | string;
  message: string;
};
export type LinkedChecklistMigrationResult = { outcomes: LinkedChecklistMigrationOutcome[] };

export const useLinkedChecklistMigrate = () =>
  useMutation<LinkedChecklistMigrationResult, unknown, LinkedChecklistMigrateRequest>({
    mutationFn: async (request) => {
      const response = await axiosClient.post("/api/linked-checklist/migrate", request);
      return response.data;
    },
    onError: (error) => handleApiError(error, "Linked-checklist migration failed"),
  });

// --- Pipeline: parents deferred until their migrated children are published on the target. ---
export type LinkedPipelineChildView = {
  sourceId: string;
  name: string | null;
  action: LinkedChecklistAction;
  targetId: string | null;
  /** target facility a MIGRATE child was imported into (publish is re-checked there); null for LINK_EXISTING. */
  facilityId: string | null;
  useCaseId: string | null;
  published: boolean;
};
export type LinkedPipelineStatus = "WAITING_FOR_PUBLISH" | "READY" | "MIGRATED" | "FAILED";
export type LinkedMigrationPipelineEntry = {
  id: number;
  sourceServiceId: number;
  sourceFacilityId: string;
  targetServiceId: number;
  targetFacilityId: string;
  targetUseCaseId: string | null;
  targetAuthorIds: string | null;
  parentChecklistId: string;
  parentChecklistName: string | null;
  children: LinkedPipelineChildView[];
  status: LinkedPipelineStatus | string;
  statusMessage: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

/** Active deferred parents; each read refreshes publish status (WAITING_FOR_PUBLISH / READY). */
export const useLinkedChecklistPipeline = (enabled = true) =>
  useQuery<LinkedMigrationPipelineEntry[]>({
    queryKey: ["linked-checklist", "pipeline"],
    queryFn: async () => {
      const response = await axiosClient.get("/api/linked-checklist/pipeline");
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled,
  });

/** Import a deferred parent once its children are published. */
export const useLinkedChecklistPipelineContinue = () => {
  const queryClient = useQueryClient();
  return useMutation<LinkedMigrationPipelineEntry, unknown, number>({
    mutationFn: async (id) => {
      const response = await axiosClient.post(`/api/linked-checklist/pipeline/${id}/continue`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["linked-checklist", "pipeline"] }),
    onError: (error) => handleApiError(error, "Failed to continue the deferred migration"),
  });
};

/** Drop a deferred parent from the pipeline. */
export const useLinkedChecklistPipelineCancel = () => {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, number>({
    mutationFn: async (id) => {
      await axiosClient.delete(`/api/linked-checklist/pipeline/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["linked-checklist", "pipeline"] }),
    onError: (error) => handleApiError(error, "Failed to remove the pipeline entry"),
  });
};
